// =============================================
// Reverse SERP Validator v9
// strict vs exploratory probes — candidate_sv from strict only
// suggest-first: blocked rewrite → suggest, not keep
// =============================================

import { getKeywordData, getOrganicResults, getRankedKeywords, isDataForSEOConfigured } from "@/services/dataForSeoService";
import { getLLMProvider, getStageConfig } from "@/services/llm";
import { parseLLMJSON } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import type { NodePreview, DemandTier, DocumentRole } from "@/types/thematicMaps";
import { DOCUMENT_ROLES } from "@/types/thematicMaps";
import { logPublic, logDebug, logError } from "@/services/contentEngine/contentLogger";

export type SerpValidationDecision = "keep" | "suggest" | "rewrite" | "merge" | "reject";

export interface SerpCompetitor {
  url: string; domain: string; title: string;
  ranked_keywords: Array<{ keyword: string; search_volume: number }>;
}

export type SerpMode = "informational" | "mixed" | "product_serp";
export type InferredIntent = "definition" | "how_to" | "comparison" | "troubleshooting" | "commercial" | "navigational" | "unknown";

export interface ValidatedNodePreview extends NodePreview {
  candidate_sv: number; market_keyword_sv: number; validated_sv: number;
  original_search_volume: number; original_cpc: number; original_keyword_difficulty: number;
  validated_search_volume: number; validated_cpc: number; validated_keyword_difficulty: number;
  serp_top3: SerpCompetitor[];
  market_keywords: Array<{ keyword: string; search_volume: number }>;
  serp_validation: SerpValidationDecision;
  serp_validation_reason: string;
  overlap_score: number;
  original_question: string;
  validated_question?: string; validated_title?: string;
  suggested_question?: string; suggested_reason?: string;
  validation_confidence: "high" | "medium" | "low";
  search_volume: number; cpc: number; keyword_difficulty: number;
  display_sv: number;
  demand_tier: DemandTier;
  related_keywords: Array<{ keyword: string; search_volume: number }>;
  entity_match_score: number; role_match_score: number;
  scope_similarity_score: number; rewrite_confidence_score: number;
  rewrite_block_reason: string;
  final_decision_source: "rules" | "llm" | "rules_after_llm";
  best_market_keyword: string;
  // v8 debug fields
  inferred_candidate_intent: InferredIntent;
  inferred_market_intent: InferredIntent;
  serp_mode: SerpMode;
  role_preserved: boolean;
  entity_preserved: boolean;
  keep_confidence_reason: string;
  // v9 probe debug fields
  strict_probe_winner?: string;
  strict_probe_sv?: number;
  exploratory_probe_winner?: string;
  exploratory_probe_sv?: number;
  candidate_sv_source?: "exact" | "strict_probe" | "no_strict_data";
  decision_reason_type?: string;
}

const CONCURRENT = 3;

// =============================================
// NLP
// =============================================

const STOP = new Set(["jest", "jak", "czy", "co", "to", "sie", "nie", "dla", "na", "do", "od", "za", "po",
  "ze", "przy", "lub", "albo", "oraz", "tego", "tej", "ten", "ile", "jaki", "jaka", "jakie",
  "czym", "ktory", "gdzie", "kiedy", "dlaczego", "moze", "mozna", "bardzo", "tylko",
  "and", "the", "is", "in", "of", "a", "are", "was", "be", "has"]);

const PL_DIACRITICS: Record<string, string> = { "ą":"a","ć":"c","ę":"e","ł":"l","ń":"n","ó":"o","ś":"s","ź":"z","ż":"z" };

/** Strip diacritics, lowercase, collapse spaces, strip punctuation */
function normText(s: string): string {
  return s.toLowerCase()
    .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (c) => PL_DIACRITICS[c.toLowerCase()] || c)
    .replace(/[?!.,;:'"()\[\]{}\-–—\/\\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Strip question prefixes: "co to jest", "czym jest", "jaki", "jak", "gdzie", "dlaczego" etc. */
function stripQuestionPrefix(s: string): string {
  return s
    .replace(/^(co to jest|czym jest|co to|jak dziala|na czym polega|z czego jest|jaka jest|jakie sa|jaki jest|co zawiera|gdzie jest|dlaczego|jak)\s+/i, "")
    .trim();
}

/** Normalize for head-phrase comparison: strip diacritics + question prefix + basic plural → singular */
function normHeadPhrase(s: string): string {
  let t = normText(s);
  t = stripQuestionPrefix(t);
  // Basic PL plural normalization (no heavy stemmer)
  t = t.replace(/\b(\w{3,})y\b/g, "$1a"); // nozy → noza, etc. (approximation)
  t = t.replace(/\b(\w{3,})ow\b/g, "$1"); // nozow → noz
  t = t.replace(/\b(\w{3,})ami\b/g, "$1"); // nozami → noz
  return t.replace(/\s+/g, " ").trim();
}

// Neutral wrapper tokens — only these may differ between near-exact phrases
const WRAPPER_TOKENS = new Set([
  "co", "to", "jest", "czym", "czy", "na", "do", "w", "z", "i", "oraz", "a",
  "the", "is", "of", "in", "and", "a",
]);

/** Check if two phrases are near-equivalent (same content, differ only by wrapper tokens) */
function isNearExactMatch(a: string, b: string): boolean {
  const na = normHeadPhrase(a);
  const nb = normHeadPhrase(b);
  if (na === nb) return true;

  const ta = na.split(/\s+/).filter(Boolean);
  const tb = nb.split(/\s+/).filter(Boolean);
  if (ta.length === 0 || tb.length === 0) return false;

  // Token-subset check: all tokens of shorter appear in longer
  const [shorter, longer] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  const longerSet = new Set(longer);
  if (!shorter.every((w) => longerSet.has(w))) return false;

  // Extra tokens in longer must ALL be wrapper tokens — any content word = NOT near-exact
  const shorterSet = new Set(shorter);
  const extraTokens = longer.filter((w) => !shorterSet.has(w));
  if (extraTokens.some((w) => !WRAPPER_TOKENS.has(w))) return false;

  return true;
}

/**
 * Head-keyword match for definition/glossary roles.
 * Checks if market keyword is the bare entity / head phrase of a "co to jest X?" candidate.
 */
function isHeadKeywordMatch(candidateQuestion: string, marketKeyword: string, primaryEntity: string): boolean {
  const cHead = normHeadPhrase(candidateQuestion);
  const mHead = normHeadPhrase(marketKeyword);
  const eHead = normHeadPhrase(primaryEntity);

  // market == entity head phrase (or vice versa)
  if (eHead.length >= 3 && (mHead === eHead || isTokenSubset(mHead, eHead) || isTokenSubset(eHead, mHead))) return true;
  // market == candidate head phrase
  if (cHead.length >= 3 && (mHead === cHead || isTokenSubset(mHead, cHead) || isTokenSubset(cHead, mHead))) return true;

  return false;
}

/** All tokens of a appear in b (safe: no semantic token guard needed here, caller checks role) */
function isTokenSubset(a: string, b: string): boolean {
  const ta = a.split(/\s+/).filter(Boolean);
  const tb = new Set(b.split(/\s+/).filter(Boolean));
  return ta.length > 0 && ta.every((w) => tb.has(w));
}

function contentWords(text: string): Set<string> {
  return new Set(text.toLowerCase().replace(/[?!.,;:'"()\[\]{}\-–—\/\\]/g, " ")
    .split(/\s+/).filter((w) => w.length > 2 && !STOP.has(w)));
}

function overlap(a: string, b: string): number {
  const wa = contentWords(a), wb = contentWords(b);
  if (wa.size === 0 || wb.size === 0) return 0;
  let n = 0; for (const w of wa) if (wb.has(w)) n++;
  return n / Math.max(wa.size, wb.size);
}

// =============================================
// Intent classifier — infer intent from query text
// =============================================

function inferIntent(text: string): InferredIntent {
  const t = text.toLowerCase();
  // Definition FIRST — "z czego robi się", "z czego jest", "jak działa", "na czym polega" = definition, NOT how_to
  if (/z czego (jest|robi|sklada|powstaje)|na czym polega|jak dziala(?! krok)|czym jest|co to jest|co to\b|definicj|co oznacza|jaka jest|jakie sa|jaki jest|co zawiera/i.test(t)) return "definition";
  // How_to — actionable instructions (including "jaki/jakie/jaka ... wybrać" = selection/recommendation, NOT comparison)
  if (/\bjak\b.*(zrob|przygotow|uzywa|wybra|ugotow|pokroi|formow|zwij|nawilz|ostrzyz|rozmroz|przechow|podaw)/i.test(t)) return "how_to";
  if (/\b(jaki|jaka|jakie)\b.*(wybra|dobra|kupic|uzywac|stosowac)/i.test(t)) return "how_to";
  // Troubleshooting
  if (/\bdlaczego\b|\bczemu\b|co zrobic gdy|co zrobic jak|nie dziala|problem|blad|za mokr|za such|peka|rozpada|kruszyj|rozklej|twardniej/i.test(t)) return "troubleshooting";
  // Comparison — requires explicit 2-entity comparison structure (X vs Y, X czy Y, różnice)
  if (/\bvs\b|versus|porówn|porown|różnic|roznic|\bczy\b.*\bczy\b/i.test(t)) return "comparison";
  // Commercial
  if (/\bkup\b|cena|sklep|zamów|gdzie kupic|ile kosztuj/i.test(t)) return "commercial";
  return "unknown";
}

/** Check if intents match. unknown = can't determine = block rewrite, allow suggest */
function intentsMatch(a: InferredIntent, b: InferredIntent): { match: boolean; allowRewrite: boolean } {
  if (a === "unknown" || b === "unknown") return { match: true, allowRewrite: false };
  if (a === b) return { match: true, allowRewrite: true };
  return { match: false, allowRewrite: false };
}

/** Intent compatibility matrix — which market intents are acceptable for which document roles */
const INTENT_COMPAT: Record<string, Set<InferredIntent>> = {
  definition:      new Set(["definition"] as InferredIntent[]),
  glossary:        new Set(["definition"] as InferredIntent[]),
  history:         new Set(["definition"] as InferredIntent[]),
  how_to:          new Set(["how_to"] as InferredIntent[]),
  guide:           new Set(["how_to"] as InferredIntent[]),
  protocol:        new Set(["how_to"] as InferredIntent[]),
  troubleshooting: new Set(["troubleshooting", "how_to"] as InferredIntent[]),
  comparison:      new Set(["comparison"] as InferredIntent[]),
  commercial:      new Set(["commercial", "comparison"] as InferredIntent[]),
  // unknown role → nothing compatible (conservative)
};

/** Is market keyword's intent compatible with candidate's document_role? */
function isIntentCompatible(candidateRole: string, marketKeyword: string): boolean {
  const marketIntent = inferIntent(marketKeyword);
  if (marketIntent === "unknown") return false; // unknown never confirms
  const allowed = INTENT_COMPAT[candidateRole];
  if (!allowed) return true; // unknown role = don't block
  return allowed.has(marketIntent);
}

// =============================================
// SERP mode — detect product/ecommerce SERPs
// =============================================

const ECOMMERCE_DOMAINS = new Set([
  "allegro.pl", "ceneo.pl", "empik.com", "amazon", "ebay", "sklep", "shop",
  "morele.net", "mediamarkt.pl", "mediaexpert.pl", "x-kom.pl", "olx.pl",
]);

function detectSerpMode(competitors: SerpCompetitor[]): SerpMode {
  if (competitors.length === 0) return "informational";
  let productCount = 0;
  for (const c of competitors) {
    const d = c.domain.toLowerCase();
    const t = c.title.toLowerCase();
    if (ECOMMERCE_DOMAINS.has(d) || /sklep|kup|cena|zł|pln|dodaj do koszyka/i.test(t) || /\/produkt|\/product|\/p\/|\/kategori/i.test(c.url)) {
      productCount++;
    }
  }
  if (productCount >= 2) return "product_serp";
  if (productCount >= 1) return "mixed";
  return "informational";
}

// =============================================
// Improved overlap — entity-weighted, penalize single generic word
// =============================================

function weightedOverlap(a: string, b: string, primaryEntity: string): number {
  const wa = contentWords(a), wb = contentWords(b);
  if (wa.size === 0 || wb.size === 0) return 0;
  const entityWords = contentWords(primaryEntity);

  let score = 0;
  let total = 0;
  for (const w of wa) {
    const weight = entityWords.has(w) ? 2.0 : 1.0; // entity words count double
    total += weight;
    if (wb.has(w)) score += weight;
  }

  const raw = total > 0 ? score / total : 0;

  // Penalty: if only 1 generic word matches, it's weak
  let matchCount = 0;
  for (const w of wa) if (wb.has(w)) matchCount++;
  if (matchCount <= 1 && wa.size > 2) return raw * 0.5;

  return raw;
}

function normQ(q: string): string {
  return q.toLowerCase().replace(/[?!.,;:'"()\[\]{}\-–—\/\\]/g, "").replace(/\s+/g, " ").trim();
}

// =============================================
// Entity matching — GENERIC, no hardcoded niches
// =============================================

// Generic category/broadening words (any niche)
const CATEGORY_WORDS = new Set([
  "rodzaje", "typy", "kategorie", "produkty", "dodatki", "skladniki", "narzedzia",
  "artykuly", "poradnik", "lista", "ranking", "zestawienie", "akcesoria", "materialy",
  "metody", "techniki", "sposoby", "zasady", "podstawy", "wprowadzenie", "przeglad",
  "types", "kinds", "categories", "products", "tools", "accessories", "methods", "basics",
  "guide", "overview", "list", "review", "comparison", "alternatives",
]);

/** Normalize word for matching: lowercase, strip PL diacritics, basic lemma */
function normWord(w: string): string {
  const PL: Record<string, string> = { "ą":"a","ć":"c","ę":"e","ł":"l","ń":"n","ó":"o","ś":"s","ź":"z","ż":"z" };
  return w.toLowerCase().replace(/[ąćęłńóśźż]/g, (c) => PL[c] || c);
}

/**
 * Entity score — GENERIC, works for any niche.
 * Measures how well the market keyword preserves the candidate's primary entity.
 * Uses normText for diacritic-insensitive comparison + head phrase matching.
 */
function entityScore(text: string, entity: string, required: string[], _compTitles: string[]): number {
  const tNorm = normText(text);
  const eNorm = normText(entity);

  // Quick check: normalized head phrases — token-subset match → 1.0
  const tHead = stripQuestionPrefix(tNorm);
  const eHead = stripQuestionPrefix(eNorm);
  if (eHead.length >= 3 && isNearExactMatch(tHead, eHead)) return 1.0;

  // Tokenize market text for token-level matching
  const tTokens = tNorm.split(/\s+/).filter(Boolean);

  // Extract core entity words (>= 3 chars, not stop) — diacritics already stripped
  const coreWords = eNorm.split(/\s+/).filter((w) => w.length >= 3 && !STOP.has(w));
  if (coreWords.length === 0) return 0.5; // very short entity, can't check

  // Count how many core entity words appear in market text (token-level, no raw substring)
  const matched = coreWords.filter((w) => {
    // Exact token match
    if (tTokens.includes(w)) return true;
    // Stem/prefix match: min 4-char prefix to avoid false positives
    const stem = w.length >= 5 ? w.slice(0, -1) : w;
    if (stem.length >= 4 && tTokens.some((tw) => tw.startsWith(stem))) return true;
    return false;
  });
  const coverage = matched.length / coreWords.length;

  // Strong match: >= 80% core words present
  if (coverage >= 0.8) return 1.0;
  // Partial match: >= 50%
  if (coverage >= 0.5) return 0.7;
  // Weak: some match but not enough for rewrite
  if (coverage > 0) return 0.4;

  // Fallback: check required_entities — if market has required entity words
  const tw = contentWords(text);
  for (const re of required) {
    const rw = contentWords(re);
    if (rw.size === 0) continue;
    let m = 0; for (const w of rw) if (tw.has(w)) m++;
    if (m / rw.size > 0.6) return 0.35;
  }

  // No match
  return 0.0;
}

/**
 * Generic broadening detection — no hardcoded pairs.
 * Detects when market keyword replaces specific entity with broader category.
 */
function isBroadening(entity: string, market: string): boolean {
  const e = normWord(entity);
  const m = normWord(market);
  const eWords = e.split(/\s+/).filter((w) => w.length >= 3 && !STOP.has(w));
  const mWords = m.split(/\s+/).filter((w) => w.length >= 3 && !STOP.has(w));

  // If entity core words are MISSING from market → potential broadening (token-level)
  const entityInMarket = eWords.filter((w) => mWords.includes(w));
  if (eWords.length > 0 && entityInMarket.length === 0) {
    // Entity completely absent from market keyword
    // Check if market uses category words instead
    const hasCategoryWord = mWords.some((w) => CATEGORY_WORDS.has(w));
    if (hasCategoryWord) return true;
  }

  // Market is shorter and more generic (fewer content words)
  if (mWords.length < eWords.length * 0.5 && mWords.length <= 2) {
    return true; // market is too short/generic
  }

  return false;
}

function roleScore(candidateQ: string, targetQ: string, role: string): number {
  const t = targetQ.toLowerCase(), c = candidateQ.toLowerCase();
  const def = /czym|co to|co jest|definicj/;
  const how = /jak |krok po|instrukcj|sposób|sposob|technik/;
  const cmp = /vs |versus |porówn|porown|różnic|roznic|kiedy wybr/;
  const tsh = /dlaczego |co zrobić|co zrobic|problem/;

  if (role === "definition" && cmp.test(t) && !cmp.test(c)) return 0.3;
  if (role === "definition" && how.test(t) && !how.test(c) && !def.test(t)) return 0.4;
  if (role === "comparison" && !cmp.test(t) && cmp.test(c)) return 0.3;
  if (role === "how_to" && def.test(t) && !how.test(t)) return 0.4;
  if (role === "troubleshooting" && !tsh.test(t) && tsh.test(c)) return 0.3;
  return 1.0;
}

function scopeScore(candidateQ: string, targetQ: string): number {
  const cw = contentWords(candidateQ), tw = contentWords(targetQ);
  if (cw.size === 0) return 0;
  let n = 0; for (const w of cw) if (tw.has(w)) n++;
  return n / cw.size;
}

// =============================================
// findBestSuggestion — evaluate ALL market keywords, not just top
// =============================================

interface SuggestionCandidate {
  keyword: string;
  sv: number;
  eScore: number;
  rScore: number;
  sScore: number;
  overlap: number;
  confidence: number;
}

function findBestSuggestion(
  candidate: NodePreview,
  marketKws: Array<{ keyword: string; search_volume: number }>,
  compTitles: string[],
): SuggestionCandidate | null {
  if (marketKws.length === 0) return null;

  const candidates: SuggestionCandidate[] = [];
  for (const mk of marketKws.slice(0, 15)) {
    if (isBroadening(candidate.primary_entity, mk.keyword)) continue;

    const eS = entityScore(mk.keyword, candidate.primary_entity, candidate.required_entities || [], compTitles);
    const rS = roleScore(candidate.canonical_question, mk.keyword, candidate.document_role);
    const sS = scopeScore(candidate.canonical_question, mk.keyword);
    const ov = overlap(candidate.canonical_question, mk.keyword);

    // PRE-FILTER: entity must have meaningful match
    if (eS < 0.5) continue;
    // PRE-FILTER: intent must be compatible with candidate role
    if (!isIntentCompatible(candidate.document_role, mk.keyword)) continue;
    // PRE-FILTER: scope gate — market kw must carry at least 1 scope word from canonical question
    const candidateContent = contentWords(candidate.canonical_question);
    const entityContent = contentWords(candidate.primary_entity);
    const scopeWords = new Set([...candidateContent].filter((w) => !entityContent.has(w)));
    if (scopeWords.size > 0) {
      const marketContent = contentWords(mk.keyword);
      const scopeHits = [...scopeWords].filter((w) => marketContent.has(w)).length;
      if (scopeHits === 0) continue; // market kw has zero scope words → not relevant
    }
    // PRE-FILTER: some lexical overlap required
    if (ov < 0.15 && sS < 0.2) continue;

    const conf = eS * 0.30 + rS * 0.20 + sS * 0.25 + ov * 0.15 + Math.min(mk.search_volume / 5000, 1) * 0.10;
    candidates.push({ keyword: mk.keyword, sv: mk.search_volume, eScore: eS, rScore: rS, sScore: sS, overlap: ov, confidence: Math.round(conf * 100) / 100 });
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.confidence - a.confidence);
  return candidates[0];
}

// Decision thresholds
const REWRITE_MIN = 0.70;
const SUGGEST_MIN = 0.35;

function isJunk(kw: string): boolean {
  if (kw.trim().length < 3 || /^\d[\d\s:\/.,]+$/.test(kw) || kw.split(/\s+/).length < 2) return true;
  return false;
}

function filterTopical(kws: Array<{ keyword: string; search_volume: number }>, q: string, entity: string) {
  const topic = new Set([...contentWords(q), ...contentWords(entity)]);
  return kws.filter((k) => !isJunk(k.keyword)).map((k) => {
    const w = contentWords(k.keyword);
    let n = 0; for (const x of w) if (topic.has(x)) n++;
    return { ...k, overlap: w.size > 0 ? n / w.size : 0 };
  }).filter((k) => k.overlap > 0).sort((a, b) => b.search_volume - a.search_volume);
}

function assignTier(sv: number): DemandTier {
  return sv > 1000 ? "clearly_validated" : sv >= 100 ? "validated_moderate" : sv >= 10 ? "weak_signal" : "no_demand";
}

// =============================================
// Normalize node language — simplify, split 2-intent nodes, naturalize
// =============================================

export async function normalizeNodeLanguage(
  candidates: NodePreview[],
  language: string,
): Promise<NodePreview[]> {
  if (candidates.length === 0) return candidates;

  const cfg = getStageConfig("fan_out");
  const llm = getLLMProvider(cfg.provider);

  const nodesStr = candidates.map((n, i) =>
    `${i + 1}. title: "${n.title}" | question: "${n.canonical_question}" | entity: "${n.primary_entity}" | role: "${n.document_role}"`
  ).join("\n");

  const prompt = `Popraw te candidate nodes — normalizuj jezyk, ale NIE upraszczaj zakresu.

## CO WOLNO:
- Poprawic interpunkcje (dodac "?" na koncu)
- Zamienic sztuczne formy: "protokol" → "jak", "checklista" → "co trzeba"
- Poprawic oczywiste bledy jezykowe i polskie znaki diakrytyczne
- Rozbic node TYLKO jesli laczy 2 ROZNE document_role w jednym pytaniu:
  ROZBIJ: "Co to jest X i jak go uzywac?" → definition + how_to = 2 node'y
  ROZBIJ: "Co to jest X i jak porownac z Y?" → definition + comparison = 2 node'y
  ZOSTAW: "Jak ugotowac i doprawic ryz?" → oba kroki to how_to = 1 node
  ZOSTAW: "Dlaczego X sie psuje i jak to naprawic?" → troubleshooting = 1 node

## DOZWOLONE document_role (TYLKO te, nic innego):
definition, how_to, comparison, troubleshooting, glossary, history, commercial, protocol, guide

## CZEGO NIE WOLNO:
- NIE skracaj canonical_question tylko po to by bylo krotsze
- NIE usuwaj qualifierow ("do sushi", "w Japonii", "sushi") jesli sa potrzebne do jednoznacznosci
- NIE rozbijaj node'a jesli oba tematy maja TE SAMA document_role
  (np. "Jak ugotowac i doprawic ryz do sushi?" → oba how_to = ZOSTAW)
  (np. "Dlaczego sushi sie rozpada i jak to naprawic?" → oba troubleshooting = ZOSTAW)
- NIE zamieniaj bogatszego pytania na ubozsze
- NIE zmieniaj document_role ani primary_entity

## ZACHOWAJ:
- primary_entity — bez zmian
- document_role — bez zmian
- required_entities — bez zmian
- slug: male litery, myslniki, bez polskich znakow, max 60 znakow
- canonical_question MUSI konczyc sie "?"
- Jezyk: ${language}

## Nodes do normalizacji:
${nodesStr}

Zwroc JSON array (moze miec WIECEJ elementow niz input TYLKO jesli rozbijales niezalezne search intenty):
[{ "original_index": 1, "title": "...", "slug": "...", "canonical_question": "...", "document_role": "...", "primary_entity": "...", "primary_intent": "...", "required_entities": ["..."] }]`;

  try {
    const resp = await llm.call(prompt, { model: cfg.model, temperature: 0.2 });
    const parsed = parseLLMJSON<any>(resp.text);
    const arr = Array.isArray(parsed) ? parsed : parsed?.results || parsed?.nodes || [];

    if (arr.length === 0) return candidates;

    // Build normalized nodes
    const normalized: NodePreview[] = [];
    let invalidRoleCount = 0;
    for (const item of arr) {
      const origIdx = (item.original_index || 1) - 1;
      const orig = candidates[Math.min(origIdx, candidates.length - 1)] || candidates[0];

      // Walidacja document_role: LLM czasem wymysla role spoza enum (np. "explanation",
      // "clarification"). DB ma CHECK constraint na 9 rol — nieznana rola → 23514.
      // Jesli LLM zwrocil nieznana → fallback do orig.document_role (znana i poprawna).
      const rawRole = String(item.document_role ?? "").toLowerCase().trim().replace(/[-\s]+/g, "_");
      let role: DocumentRole = orig.document_role;
      if (rawRole && DOCUMENT_ROLES.includes(rawRole as DocumentRole)) {
        role = rawRole as DocumentRole;
      } else if (rawRole && rawRole !== orig.document_role) {
        invalidRoleCount++;
        logDebug("NormalizeNodes", `invalid role from LLM "${item.document_role}" → fallback "${orig.document_role}"`);
      }

      normalized.push({
        title: item.title || orig.title,
        slug: item.slug || orig.slug,
        document_role: role,
        primary_intent: item.primary_intent || orig.primary_intent,
        primary_entity: item.primary_entity || orig.primary_entity,
        canonical_question: item.canonical_question || orig.canonical_question,
        required_entities: item.required_entities || orig.required_entities,
        canonical_question_hash: "", // will be re-hashed by dedup
        duplicate_level: "unique",
      });
    }
    if (invalidRoleCount > 0) {
      logPublic("NormalizeNodes", `fallback ${invalidRoleCount}/${arr.length} invalid document_role from LLM → orig role`);
    }

    logPublic("NormalizeNodes", `${candidates.length} → ${normalized.length} nodes`);
    return normalized;
  } catch (err) {
    logError("NormalizeNodes", "Failed, using originals", err);
    return candidates;
  }
}

// =============================================
// Classified probes — strict (for candidate_sv) vs exploratory (for suggest only)
// =============================================

interface ClassifiedProbe {
  query: string;
  kind: "exact" | "strict" | "exploratory";
}

interface ProbeSVResult {
  strict_sv: number; strict_cpc: number; strict_kd: number; strict_winner: string;
  exploratory_sv: number; exploratory_winner: string;
  probes: string[];
}

function generateProbeQueries(canonicalQuestion: string, primaryEntity: string, documentRole?: string): ClassifiedProbe[] {
  const probes: ClassifiedProbe[] = [];
  const q = canonicalQuestion.toLowerCase().replace(/[?!]/g, "").trim();
  const entity = primaryEntity.toLowerCase().trim();
  const role = (documentRole || "").toLowerCase();

  // Probe 1: exact normalized question — always exact
  probes.push({ query: q, kind: "exact" });

  // Role-specific probes — strict (preserve intent + entity)
  if (role === "definition" || role === "glossary") {
    probes.push({ query: `co to jest ${entity}`, kind: "strict" });
    probes.push({ query: `czym jest ${entity}`, kind: "strict" });
    probes.push({ query: `${entity} co to`, kind: "strict" });
  } else if (role === "how_to" || role === "protocol" || role === "guide") {
    probes.push({ query: `jak ${entity}`, kind: "strict" });
    probes.push({ query: `jak zrobic ${entity}`, kind: "strict" });
    probes.push({ query: `jak przygotowac ${entity}`, kind: "strict" });
  } else if (role === "troubleshooting") {
    probes.push({ query: `dlaczego ${entity}`, kind: "strict" });
    probes.push({ query: `co zrobic gdy ${entity}`, kind: "strict" });
    probes.push({ query: `${entity} problem`, kind: "strict" });
  } else if (role === "comparison") {
    const match = q.match(/(\w{3,})\s+(?:vs|a|czy|versus)\s+(\w{3,})/);
    if (match) {
      probes.push({ query: `${match[1]} vs ${match[2]}`, kind: "strict" });
      probes.push({ query: `${match[1]} a ${match[2]}`, kind: "strict" });
      probes.push({ query: `roznica ${match[1]} ${match[2]}`, kind: "strict" });
    } else {
      probes.push({ query: entity, kind: "exploratory" });
    }
  } else if (role === "history") {
    probes.push({ query: `historia ${entity}`, kind: "strict" });
    probes.push({ query: `${entity} historia`, kind: "strict" });
    probes.push({ query: `${entity} pochodzenie`, kind: "strict" });
  } else if (role === "commercial") {
    probes.push({ query: `${entity} cena`, kind: "strict" });
    probes.push({ query: `${entity} sklep`, kind: "strict" });
    probes.push({ query: `gdzie kupic ${entity}`, kind: "strict" });
  } else {
    // Fallback: bare entity = exploratory, entity + modifiers = strict
    probes.push({ query: entity, kind: "exploratory" });
    const words = contentWords(q);
    const entityWords = new Set(contentWords(entity));
    const mods: string[] = [];
    for (const w of words) if (!entityWords.has(w) && mods.length < 2) mods.push(w);
    if (mods.length > 0) probes.push({ query: `${entity} ${mods.join(" ")}`, kind: "strict" });
  }

  // Deduplicate by query string + cleanup
  const seen = new Set<string>();
  const deduped: ClassifiedProbe[] = [];
  for (const p of probes) {
    const normalized = p.query.trim().toLowerCase();
    if (normalized.length >= 3 && !seen.has(normalized)) {
      seen.add(normalized);
      deduped.push({ query: normalized, kind: p.kind });
    }
  }
  return deduped.slice(0, 6);
}

// =============================================
// Regenerate final node — natural Polish from market query
// =============================================

async function regenerateFinalNode(
  nodes: ValidatedNodePreview[],
  language: string,
): Promise<void> {
  const eligible = nodes.filter((n) =>
    (n.serp_validation === "rewrite" || n.serp_validation === "suggest") &&
    (n.validated_question || n.suggested_question || n.best_market_keyword)
  );

  if (eligible.length === 0) return;

  const cfg = getStageConfig("fan_out");
  const llm = getLLMProvider(cfg.provider);

  const str = eligible.map((n, i) => {
    const mktKw = n.validated_question || n.suggested_question || n.best_market_keyword;
    const compTitles = n.serp_top3.map((c) => c.title).filter(Boolean).slice(0, 3).join("; ");
    return `${i + 1}. Candidate: "${n.canonical_question}" | Entity: "${n.primary_entity}" | Role: "${n.document_role}"\n` +
      `   Market keyword: "${mktKw}" (SV=${n.market_keyword_sv})\n` +
      `   Competitor titles: ${compTitles || "brak"}`;
  }).join("\n\n");

  const prompt = `Masz candidate nodes i dane z rynku. Wygeneruj naturalny polski node.

## ZASADY:
- market_query: krotka fraza jak rynek szuka (max 6 slow, bez "?")
- canonical_question: naturalne polskie pytanie (max 12 slow, konczy sie "?")
- title: czytelny tytul artykulu (max 8 slow)
- ZACHOWAJ primary_entity — nie zamieniaj na szersza kategorie
- ZACHOWAJ document_role
- NIE poszerzaj tematu
- Jezyk: ${language}

## Nodes
${str}

Zwroc JSON array:
[{ "index": 1, "market_query": "...", "canonical_question": "...", "title": "..." }]`;

  try {
    const resp = await llm.call(prompt, { model: cfg.model, temperature: 0.2 });
    const arr = (() => { const p = parseLLMJSON<any>(resp.text); return Array.isArray(p) ? p : p?.results || []; })();

    for (const r of arr) {
      const i = r.index - 1;
      if (i < 0 || i >= eligible.length) continue;
      const n = eligible[i];

      if (r.market_query) (n as any).market_query = r.market_query;

      // Update validated/suggested question with natural form
      if (r.canonical_question && n.serp_validation === "rewrite") {
        n.validated_question = r.canonical_question;
        n.validated_title = r.title || n.title;
      } else if (r.canonical_question && n.serp_validation === "suggest") {
        n.suggested_question = r.canonical_question;
        n.suggested_reason = `Naturalna forma: ${r.canonical_question}`;
      }
    }
  } catch (err) {
    logError("RegenerateFinalNode", "Failed", err);
  }
}

// =============================================
// Main
// =============================================

export async function lightPass(
  candidates: NodePreview[], language: string, locationCode: number,
  onProgress?: (step: string) => void,
): Promise<ValidatedNodePreview[]> {
  if (!isDataForSEOConfigured() || candidates.length === 0)
    return candidates.map((c) => makeNeutral(c, "DataForSEO niedostepne"));

  const locName = locationCode === 2616 ? "Poland" : "United States";
  const langName = language === "pl" ? "Polish" : "English";

  // Multi-probe SV: strict probes for candidate_sv, exploratory for suggest only
  onProgress?.("Generowanie probe queries...");
  const allProbeQueries: string[] = [];
  const probeMap = new Map<string, { probes: ClassifiedProbe[]; nodeIdx: number }>();

  for (let idx = 0; idx < candidates.length; idx++) {
    const c = candidates[idx];
    const probes = generateProbeQueries(c.canonical_question, c.primary_entity, c.document_role);
    probeMap.set(c.canonical_question.toLowerCase(), { probes, nodeIdx: idx });
    for (const p of probes) allProbeQueries.push(p.query);
  }

  onProgress?.(`Batch SV/CPC/KD (${allProbeQueries.length} probe queries)...`);
  let kwData: Array<{ keyword: string; search_volume: number; cpc: number; keyword_difficulty: number }> = [];
  try { kwData = await getKeywordData(allProbeQueries, locationCode, language); } catch {}
  const probeResults = new Map<string, { sv: number; cpc: number; kd: number }>();
  for (const k of kwData) probeResults.set(k.keyword.toLowerCase(), { sv: k.search_volume || 0, cpc: k.cpc || 0, kd: k.keyword_difficulty || 0 });

  // For each node, pick MAX SV separately for strict (exact+strict) and exploratory
  const svMap = new Map<string, ProbeSVResult>();
  for (const [q, { probes }] of probeMap) {
    let bestStrict = { sv: 0, cpc: 0, kd: 0, winner: "" };
    let bestExploratory = { sv: 0, winner: "" };
    for (const p of probes) {
      const r = probeResults.get(p.query.toLowerCase());
      if (!r) continue;
      if (p.kind === "exact" || p.kind === "strict") {
        if (r.sv > bestStrict.sv) bestStrict = { sv: r.sv, cpc: r.cpc, kd: r.kd, winner: p.query };
      } else {
        if (r.sv > bestExploratory.sv) bestExploratory = { sv: r.sv, winner: p.query };
      }
    }
    svMap.set(q, {
      strict_sv: bestStrict.sv, strict_cpc: bestStrict.cpc, strict_kd: bestStrict.kd, strict_winner: bestStrict.winner,
      exploratory_sv: bestExploratory.sv, exploratory_winner: bestExploratory.winner,
      probes: probes.map((p) => `[${p.kind}] ${p.query}`),
    });
  }

  const results: ValidatedNodePreview[] = [];
  for (let i = 0; i < candidates.length; i += CONCURRENT) {
    const batch = candidates.slice(i, i + CONCURRENT);
    onProgress?.(`SERP check ${Math.min(i + CONCURRENT, candidates.length)}/${candidates.length}...`);
    const br = await Promise.all(batch.map((c) => processNode(c, svMap, locationCode, language, locName, langName)));
    results.push(...br);
  }

  // LLM rewrite for rewrite+suggest
  const rwCandidates = results.filter((n) => (n.serp_validation === "rewrite" || n.serp_validation === "suggest") && n.market_keywords.length > 0);
  if (rwCandidates.length > 0) {
    onProgress?.(`LLM przepisuje ${rwCandidates.length} fraz...`);
    await llmRewrite(rwCandidates, language);
  }

  // Regenerate final nodes — natural Polish from market query
  onProgress?.("Regeneracja naturalnych polskich node'ow...");
  await regenerateFinalNode(results, language);

  // Batch validated SV
  const rewritten = results.filter((n) => n.validated_question || n.suggested_question);
  if (rewritten.length > 0) {
    onProgress?.(`SV dla ${rewritten.length} przepisanych fraz...`);
    try {
      const qs = rewritten.map((n) => n.validated_question || n.suggested_question!);
      const vs = await getKeywordData(qs, locationCode, language);
      const vm = new Map<string, number>();
      for (const k of vs) vm.set(k.keyword.toLowerCase(), k.search_volume || 0);
      for (const n of rewritten) {
        const q = (n.validated_question || n.suggested_question || "").toLowerCase();
        n.validated_sv = vm.get(q) || 0;
        n.validated_search_volume = n.validated_sv;
      }
    } catch {}
  }

  onProgress?.("Second dedup...");
  secondDedup(results);

  onProgress?.("Cross-SERP cleanup...");
  const clusterPairs = crossSerpCleanup(results);

  if (clusterPairs.length > 0) {
    onProgress?.(`LLM cluster dedup (${clusterPairs.length} par)...`);
    await llmClusterDedup(results, clusterPairs);
  }

  const s = { keep: 0, suggest: 0, rewrite: 0, reject: 0, merge: 0 };
  for (const n of results) s[n.serp_validation as keyof typeof s]++;
  logPublic("ReverseSERP", `${s.keep} keep, ${s.suggest} suggest, ${s.rewrite} rewrite, ${s.reject} reject`);
  return results;
}

// =============================================
// Process single node — suggest-first policy
// =============================================

async function processNode(
  c: NodePreview, svMap: Map<string, ProbeSVResult>,
  locCode: number, lang: string, locName: string, langName: string,
): Promise<ValidatedNodePreview> {
  // FIX 3: Role fixup — "jaki/jakie X wybrać" is how_to, not comparison (LLM sometimes misassigns)
  if (c.document_role === "comparison" && /\b(jaki|jaka|jakie)\b/i.test(c.canonical_question) && !/\bvs\b|\bczy\b.*\bczy\b|versus|roznic|porówn|porown/i.test(c.canonical_question)) {
    c = { ...c, document_role: "how_to" };
  }

  const orig: ProbeSVResult = svMap.get(c.canonical_question.toLowerCase()) || { strict_sv: 0, strict_cpc: 0, strict_kd: 0, strict_winner: "", exploratory_sv: 0, exploratory_winner: "", probes: [] };
  const cSV = orig.strict_sv; // v9: candidate_sv from strict probes only

  try {
    const serp = await getOrganicResults(c.canonical_question, locCode, lang, 10);
    const organic = (serp as any[]).filter((i) => i.type === "organic").slice(0, 3);

    if (organic.length === 0 && cSV === 0) {
      return makeNeutral(c, "Brak organic SERP + SV=0", "reject", "high", orig, "no_market_data");
    }

    // Ranked Keywords
    const comps: SerpCompetitor[] = [];
    const rawMkt: Array<{ keyword: string; search_volume: number }> = [];
    const compTitles: string[] = [];
    for (const item of organic) {
      const url = item.url || "";
      const domain = item.domain || url.replace(/https?:\/\//, "").split("/")[0];
      compTitles.push(item.title || "");
      let rk: Array<{ keyword: string; search_volume: number }> = [];
      try {
        const ranked = await getRankedKeywords(url, locName, langName, 20);
        rk = ranked.filter((r) => r.search_volume > 0).sort((a, b) => b.search_volume - a.search_volume)
          .slice(0, 20).map((r) => ({ keyword: r.keyword, search_volume: r.search_volume }));
      } catch {}
      comps.push({ url, domain, title: item.title || "", ranked_keywords: rk });
      for (const k of rk) if (!rawMkt.some((m) => m.keyword.toLowerCase() === k.keyword.toLowerCase())) rawMkt.push(k);
    }

    const filtered = filterTopical(rawMkt, c.canonical_question, c.primary_entity);

    // Find best suggestion — evaluate ALL market keywords
    const best = findBestSuggestion(c, filtered, compTitles);

    // v8: Intent + SERP mode + weighted overlap
    const candidateIntent = inferIntent(c.canonical_question);
    const serpMode = detectSerpMode(comps);

    // Best overlap — only from intent-compatible keywords
    let bestOverlap = { keyword: "", sv: 0, ov: 0, intentCompat: false };
    for (const mk of filtered) {
      const ov = weightedOverlap(c.canonical_question, mk.keyword, c.primary_entity);
      const compat = isIntentCompatible(c.document_role, mk.keyword);
      if (compat && (ov > bestOverlap.ov || (ov === bestOverlap.ov && mk.search_volume > bestOverlap.sv)))
        bestOverlap = { keyword: mk.keyword, sv: mk.search_volume, ov, intentCompat: true };
    }
    // Fallback: if no intent-compatible keyword, pick highest overlap anyway (for logging)
    if (!bestOverlap.keyword) {
      for (const mk of filtered) {
        const ov = weightedOverlap(c.canonical_question, mk.keyword, c.primary_entity);
        if (ov > bestOverlap.ov) bestOverlap = { keyword: mk.keyword, sv: mk.search_volume, ov, intentCompat: false };
      }
    }

    const marketIntent = bestOverlap.keyword ? inferIntent(bestOverlap.keyword) : "unknown";
    const intentResult = intentsMatch(candidateIntent, marketIntent);
    const intentOk = intentResult.match && bestOverlap.intentCompat;

    // SV sanity check
    if (cSV > 0 && best && best.sv > 0 && cSV > best.sv * 20) {
      logDebug("ReverseSERP", `SV SANITY WARNING: candidate SV=${cSV} >> market SV=${best.sv} for "${c.title.slice(0, 30)}"`);
    }

    // DECISION (v9: intent-aware, SERP-mode-aware, strict-sv-aware)
    let decision: SerpValidationDecision;
    let reason: string;
    let validConf: "high" | "medium" | "low";
    let blockReason = "";
    let keepConfReason = "";
    let decisionReasonType = "";

    // Exploratory-only demand note (for reason strings when cSV=0)
    const explNote = (orig.exploratory_sv > 0 && cSV === 0)
      ? ` (exploratory "${orig.exploratory_winner}" SV=${orig.exploratory_sv} — zbyt ogolny)`
      : "";

    // Short-circuit source: best market keyword OR bestOverlap keyword (whichever is available)
    const scKw = best?.keyword || bestOverlap.keyword || "";
    const scSv = best?.sv || bestOverlap.sv || 0;

    // Short-circuit 1: Near-exact match (same phrase after normalization, no semantic drift)
    if (scKw && isNearExactMatch(c.canonical_question, scKw)) {
      decision = "keep";
      reason = `Rynek potwierdza: near-exact match "${scKw}" (SV=${scSv})`;
      validConf = "high";
      keepConfReason = "near-exact match";
      decisionReasonType = "near_exact_match";

    // Short-circuit 2: Head-keyword match for definition/glossary — bare entity confirms "co to jest X?"
    } else if (scKw && (c.document_role === "definition" || c.document_role === "glossary") && isHeadKeywordMatch(c.canonical_question, scKw, c.primary_entity)) {
      decision = "keep";
      reason = `Head-keyword match: "${scKw}" potwierdza ${c.document_role} (SV=${scSv})`;
      validConf = "high";
      keepConfReason = "head-keyword match (definition)";
      decisionReasonType = "head_keyword_match";

    } else if (!best && filtered.length === 0) {
      decision = cSV > 0 ? "keep" : (organic.length > 0 ? "keep" : "reject");
      reason = cSV > 0
        ? `Candidate ma popyt (SV=${cSV}), brak danych rynkowych do porownania`
        : `Brak market data — SERP ${organic.length > 0 ? "istnieje" : "pusty"}${explNote}`;
      validConf = cSV > 100 ? "medium" : "low";
      keepConfReason = "no market data";
      decisionReasonType = cSV > 0 ? "own_demand_no_alternative" : "no_market_data";

    } else if (bestOverlap.ov > 0.65 && intentOk) {
      decision = "keep";
      reason = `Rynek potwierdza te sama intencje (entity+intent match, overlap ${Math.round(bestOverlap.ov * 100)}%)`;
      validConf = "high";
      keepConfReason = "high overlap + compatible intent";
      decisionReasonType = "market_confirms";

    } else if (bestOverlap.ov > 0.65 && !intentOk) {
      decision = "suggest";
      reason = `Temat pokrewny ale inna intencja (${candidateIntent}→${marketIntent}): "${bestOverlap.keyword}"`;
      validConf = "low";
      decisionReasonType = "intent_mismatch_suggest";

    } else if (serpMode === "product_serp" && (c.document_role === "definition" || c.document_role === "how_to")) {
      decision = best ? "suggest" : "keep";
      reason = `SERP produktowy — nie potwierdza ${c.document_role}`;
      validConf = "low";
      keepConfReason = "product_serp for info node";
      decisionReasonType = "product_serp";

    } else if (best && best.confidence >= REWRITE_MIN && isBroadening(c.primary_entity, best.keyword)) {
      decision = "suggest";
      reason = `Broadening zablokowany: "${best.keyword}" szersza niż "${c.primary_entity}"`;
      blockReason = "entity broadening";
      validConf = "medium";
      decisionReasonType = "broadening_blocked";

    } else if (best && best.confidence >= REWRITE_MIN) {
      const bestIntentResult = intentsMatch(candidateIntent, inferIntent(best.keyword));
      const entityOk = best.eScore >= 0.7;

      if (!bestIntentResult.match) {
        decision = "suggest";
        reason = `Rewrite zablokowany: zmiana intencji. Sugestia: "${best.keyword}"`;
        blockReason = `intent mismatch`;
        validConf = "medium";
        decisionReasonType = "rewrite_blocked_suggest";
      } else if (!bestIntentResult.allowRewrite) {
        decision = "suggest";
        reason = `Sugestia (intent nieznany): "${best.keyword}" (SV=${best.sv})`;
        blockReason = "unknown intent blocks rewrite";
        validConf = "medium";
        decisionReasonType = "rewrite_blocked_suggest";
      } else if (!entityOk) {
        decision = "suggest";
        reason = `Sugestia (entity score ${best.eScore.toFixed(2)}): "${best.keyword}"`;
        blockReason = `entity score too low for rewrite: ${best.eScore.toFixed(2)}`;
        validConf = "medium";
        decisionReasonType = "rewrite_blocked_suggest";
      } else {
        decision = "rewrite";
        reason = `Rewrite (conf=${best.confidence}): "${best.keyword}" (SV=${best.sv})`;
        validConf = "high";
        decisionReasonType = "rewrite_approved";
      }

    } else if (best && best.confidence >= SUGGEST_MIN) {
      decision = "suggest";
      reason = `Sugestia (conf=${best.confidence}): "${best.keyword}" (SV=${best.sv})`;
      validConf = "medium";
      decisionReasonType = "market_suggest";

    } else if (best && best.confidence > 0.2) {
      decision = "suggest";
      reason = `Slaba sugestia (conf=${best.confidence}): "${best.keyword}" (SV=${best.sv})`;
      validConf = "low";
      decisionReasonType = "market_suggest";

    } else {
      decision = "keep";
      reason = cSV > 0
        ? `Candidate ma wlasny popyt (SV=${cSV}), brak rownowaznej alternatywy`
        : `Brak intent-kompatybilnej alternatywy rynkowej${explNote}`;
      validConf = cSV > 100 ? "medium" : "low";
      keepConfReason = cSV > 0 ? "candidate has own demand" : "no compatible market alternative";
      decisionReasonType = cSV > 0 ? "own_demand_no_alternative" : "no_market_data";
    }

    const bestKw = best?.keyword || bestOverlap.keyword || "";
    const bestSv = best?.sv || bestOverlap.sv || 0;

    // demand_tier guard: if search_volume is 0, tier must be no_demand
    const displaySv = cSV > 0 ? cSV : bestSv;
    const tier = cSV > 0 ? assignTier(cSV) : "no_demand";

    // v9: candidate_sv_source
    const exactQ = c.canonical_question.toLowerCase().replace(/[?!]/g, "").trim();
    const csvSource: "exact" | "strict_probe" | "no_strict_data" = cSV === 0
      ? "no_strict_data"
      : (orig.strict_winner === exactQ ? "exact" : "strict_probe");

    logPublic("ReverseSERP", `"${c.title.slice(0, 50)}" → ${decision} | cSV=${cSV} mSV=${bestSv} | strict="${orig.strict_winner || "—"}" expl="${orig.exploratory_winner || "—"}(${orig.exploratory_sv})" | market="${bestKw || "—"}" | conf=${best?.confidence?.toFixed(2) || "—"} | reason=${decisionReasonType}`);

    return {
      ...c,
      candidate_sv: cSV, market_keyword_sv: bestSv, validated_sv: 0,
      original_search_volume: cSV, original_cpc: orig.strict_cpc, original_keyword_difficulty: orig.strict_kd,
      validated_search_volume: 0, validated_cpc: 0, validated_keyword_difficulty: 0,
      serp_top3: comps, market_keywords: filtered.slice(0, 10),
      serp_validation: decision, serp_validation_reason: reason,
      overlap_score: bestOverlap.ov, original_question: c.canonical_question,
      validated_question: decision === "rewrite" ? bestKw : undefined,
      suggested_question: decision === "suggest" ? bestKw : undefined,
      suggested_reason: decision === "suggest" ? reason : undefined,
      validation_confidence: validConf,
      search_volume: cSV, display_sv: displaySv,
      cpc: orig.strict_cpc, keyword_difficulty: orig.strict_kd,
      demand_tier: tier,
      related_keywords: filtered.slice(0, 5),
      entity_match_score: best?.eScore || 0, role_match_score: best?.rScore || 1,
      scope_similarity_score: best?.sScore || 0, rewrite_confidence_score: best?.confidence || 0,
      rewrite_block_reason: blockReason, final_decision_source: "rules",
      best_market_keyword: bestKw,
      // v8 debug
      inferred_candidate_intent: candidateIntent,
      inferred_market_intent: marketIntent,
      serp_mode: serpMode,
      role_preserved: intentOk,
      entity_preserved: (best?.eScore || 0) >= 0.5,
      keep_confidence_reason: keepConfReason,
      // v9 probe debug
      strict_probe_winner: orig.strict_winner || undefined,
      strict_probe_sv: orig.strict_sv,
      exploratory_probe_winner: orig.exploratory_winner || undefined,
      exploratory_probe_sv: orig.exploratory_sv,
      candidate_sv_source: csvSource,
      decision_reason_type: decisionReasonType,
    };
  } catch (err) {
    return makeNeutral(c, `Error: ${err instanceof Error ? err.message : "?"}`, "keep", "low", orig, "error");
  }
}

// =============================================
// LLM constrained rewrite — suggest-aware
// =============================================

async function llmRewrite(nodes: ValidatedNodePreview[], language: string): Promise<void> {
  const cfg = getStageConfig("fan_out");
  const llm = getLLMProvider(cfg.provider);

  const str = nodes.map((n, i) =>
    `${i + 1}. Q: "${n.canonical_question}" | Entity: "${n.primary_entity}" | Role: "${n.document_role}" | Pre-decision: ${n.serp_validation}\n` +
    `   Best market keyword: "${n.best_market_keyword}" (SV=${n.market_keyword_sv})\n` +
    `   Market: ${n.market_keywords.slice(0, 3).map((k) => `"${k.keyword}"(${k.search_volume})`).join(", ")}`
  ).join("\n\n");

  const prompt = `Masz candidate nodes i frazy z rynku. Dla kazdego zdecyduj:

## ZASADY:
- ZACHOWAJ primary_entity — nie zamieniaj na szersza kategorie
- ZACHOWAJ document_role
- NIE poszerzaj tematu (wasabi → "dodatki do sushi" = ZABRONIONE)
- Jesli market keyword jest bliski ale nie pewny → decision = "suggest"
- Jesli oryginalna fraza jest OK → decision = "keep"
- Rewrite tylko jesli masz wysoka pewnosc semantyczna
- canonical_question MUSI konczyc sie "?"
- Jezyk: ${language}

## Nodes
${str}

Zwroc JSON array:
[{ "index": 1, "decision": "keep|suggest|rewrite", "new_question": "...|null", "new_title": "...|null", "reason": "..." }]`;

  logPublic("ReverseSERP", `LLM rewrite: ${nodes.length} kandydatow, model=${cfg.model}`);
  try {
    const resp = await llm.call(prompt, { model: cfg.model, temperature: 0.2 });
    logDebug("ReverseSERP", "LLM rewrite response", { prompt_length: prompt.length, response_length: resp.text.length });
    const arr = (() => { const p = parseLLMJSON<any>(resp.text); return Array.isArray(p) ? p : p?.results || []; })();

    for (const r of arr) {
      const i = r.index - 1;
      if (i < 0 || i >= nodes.length) continue;
      const n = nodes[i];
      logPublic("ReverseSERP", `  LLM[${i + 1}] "${n.canonical_question.slice(0, 40)}" → ${r.decision}${r.new_question ? " → " + r.new_question : ""} (${r.reason || ""})`);

      if (r.decision === "keep" || !r.new_question) {
        n.serp_validation = "keep";
        n.serp_validation_reason = `LLM: ${r.reason || "fraza OK"}`;
        n.validated_question = undefined;
        // Override to suggest only if suggestion is genuinely different (not near-exact / head-keyword)
        if (n.suggested_question
          && !isNearExactMatch(n.canonical_question, n.suggested_question)
          && !isHeadKeywordMatch(n.canonical_question, n.suggested_question, n.primary_entity)) {
          n.serp_validation = "suggest";
          n.serp_validation_reason = `LLM keep, ale rynek sugeruje: "${n.suggested_question}"`;
        }
        n.final_decision_source = "llm";
        continue;
      }

      // No-op
      if (normQ(r.new_question) === normQ(n.canonical_question)) {
        n.serp_validation = "keep";
        n.serp_validation_reason = "LLM potwierdza fraze";
        n.validated_question = undefined;
        if (n.suggested_question && n.suggested_question !== r.new_question
          && !isNearExactMatch(n.canonical_question, n.suggested_question)
          && !isHeadKeywordMatch(n.canonical_question, n.suggested_question, n.primary_entity)) {
          n.serp_validation = "suggest";
        }
        n.final_decision_source = "rules_after_llm";
        continue;
      }

      // Broadening check → always suggest, never rewrite
      if (isBroadening(n.primary_entity, r.new_question)) {
        n.serp_validation = "suggest";
        n.suggested_question = r.new_question;
        n.suggested_reason = "Broadening: temat szerszy niż candidate";
        n.serp_validation_reason = `LLM broadening → suggest`;
        n.rewrite_block_reason = "Entity broadening";
        n.validated_question = undefined;
        n.final_decision_source = "rules_after_llm";
        continue;
      }

      // Post-validation entity check
      const eS = entityScore(r.new_question, n.primary_entity, n.required_entities || [], n.serp_top3.map((c) => c.title));
      const rS = roleScore(n.canonical_question, r.new_question, n.document_role);
      n.entity_match_score = eS;
      n.role_match_score = rS;

      if (r.decision === "rewrite" && eS >= 0.7 && rS >= 0.8) {
        // LLM said rewrite + passes minimum bar → trust it
        n.serp_validation = "rewrite";
        n.validated_question = r.new_question;
        n.validated_title = r.new_title || n.title;
        n.serp_validation_reason = `LLM rewrite (eS=${eS.toFixed(2)} rS=${rS.toFixed(2)}): ${r.reason}`;
        n.final_decision_source = "llm";
      } else if (r.decision === "suggest" || r.new_question) {
        // LLM said suggest, or said rewrite but didn't pass → suggest
        n.serp_validation = "suggest";
        n.suggested_question = r.new_question;
        n.suggested_reason = r.reason;
        n.serp_validation_reason = `Sugestia LLM: ${r.new_question}`;
        n.validated_question = undefined;
        n.final_decision_source = "llm";
      } else {
        n.serp_validation = "keep";
        n.serp_validation_reason = `LLM: ${r.reason || "brak lepszej frazy"}`;
        n.final_decision_source = "llm";
      }
    }
  } catch (err) {
    logError("ReverseSERP", "LLM rewrite failed", err);
  }
}

// =============================================
// Second dedup
// =============================================

function secondDedup(nodes: ValidatedNodePreview[]): void {
  const seen = new Map<string, number>();
  for (let i = 0; i < nodes.length; i++) {
    const q = normQ(nodes[i].validated_question || nodes[i].canonical_question);
    const ex = seen.get(q);
    if (ex !== undefined) {
      if ((nodes[i].rewrite_confidence_score || 0) > (nodes[ex].rewrite_confidence_score || 0)) {
        nodes[ex].serp_validation = "merge";
        nodes[ex].serp_validation_reason = `Duplikat z: "${nodes[i].title}"`;
        seen.set(q, i);
      } else {
        nodes[i].serp_validation = "merge";
        nodes[i].serp_validation_reason = `Duplikat z: "${nodes[ex].title}"`;
      }
    } else seen.set(q, i);
  }
}

// =============================================
// Cross-SERP Cleanup — post-validation dedup + cluster detection
// =============================================

const SCOPE_QUALIFIERS = new Set([
  "domu", "japonii", "poczatkujacych", "weganskie", "wegetarianskie",
  "historia", "rodzaje", "tradycyjne", "domowe", "profesjonalne",
  "dzieci", "weganski", "klasyczne", "nowoczesne", "japonskie",
]);

interface ClusterPair {
  nodeA: ValidatedNodePreview;
  nodeB: ValidatedNodePreview;
}

function crossSerpCleanup(nodes: ValidatedNodePreview[]): ClusterPair[] {
  const clusterPairs: ClusterPair[] = [];

  // Only process nodes that are still keep/suggest (not already merge/reject)
  const active = nodes.filter((n) => n.serp_validation === "keep" || n.serp_validation === "suggest");
  if (active.length < 2) return clusterPairs;

  let mergedCount = 0;
  let clusterCount = 0;

  // Helper: pick winner between 2 nodes
  function pickWinner(a: ValidatedNodePreview, b: ValidatedNodePreview): [ValidatedNodePreview, ValidatedNodePreview] {
    if (a.candidate_sv !== b.candidate_sv) return a.candidate_sv > b.candidate_sv ? [a, b] : [b, a];
    const confOrder: Record<string, number> = { high: 3, medium: 2, low: 1 };
    const confA = confOrder[a.validation_confidence] || 0;
    const confB = confOrder[b.validation_confidence] || 0;
    if (confA !== confB) return confA > confB ? [a, b] : [b, a];
    if (a.overlap_score !== b.overlap_score) return a.overlap_score > b.overlap_score ? [a, b] : [b, a];
    return a.canonical_question.length <= b.canonical_question.length ? [a, b] : [b, a];
  }

  // Helper: Jaccard on normQ tokens
  function questionJaccard(a: string, b: string): number {
    const ta = new Set(normQ(a).split(/\s+/).filter(Boolean));
    const tb = new Set(normQ(b).split(/\s+/).filter(Boolean));
    if (ta.size === 0 || tb.size === 0) return 0;
    const intersection = [...ta].filter((w) => tb.has(w)).length;
    const union = new Set([...ta, ...tb]).size;
    return union > 0 ? intersection / union : 0;
  }

  // Helper: check if one question has a scope qualifier the other lacks
  function hasScopeQualifierDiff(a: string, b: string): boolean {
    const wa = new Set(normQ(a).split(/\s+/).filter(Boolean));
    const wb = new Set(normQ(b).split(/\s+/).filter(Boolean));
    for (const q of SCOPE_QUALIFIERS) {
      if ((wa.has(q) && !wb.has(q)) || (wb.has(q) && !wa.has(q))) return true;
    }
    return false;
  }

  // --- A) Title exact dedup ---
  const titleGroups = new Map<string, ValidatedNodePreview[]>();
  for (const n of active) {
    if (n.serp_validation === "merge") continue;
    const key = `${n.document_role}::${normQ(n.title)}`;
    if (!titleGroups.has(key)) titleGroups.set(key, []);
    titleGroups.get(key)!.push(n);
  }
  for (const [, group] of titleGroups) {
    if (group.length < 2) continue;
    // Sort: best first
    group.sort((a, b) => {
      const [winA] = pickWinner(a, b);
      return winA === a ? -1 : 1;
    });
    const winner = group[0];
    for (let i = 1; i < group.length; i++) {
      group[i].serp_validation = "merge";
      group[i].serp_validation_reason = `Title dedup: duplikat "${winner.title}"`;
      mergedCount++;
      logDebug("CrossSERP", `Title dedup: merged "${group[i].title}" → "${winner.title}"`);
    }
  }

  // --- B) Near-exact question dedup ---
  const stillActive = active.filter((n) => n.serp_validation !== "merge");
  for (let i = 0; i < stillActive.length; i++) {
    if (stillActive[i].serp_validation === "merge") continue;
    for (let j = i + 1; j < stillActive.length; j++) {
      if (stillActive[j].serp_validation === "merge") continue;
      const a = stillActive[i], b = stillActive[j];

      // Same role + same entity
      if (a.document_role !== b.document_role) continue;
      if (normQ(a.primary_entity) !== normQ(b.primary_entity)) continue;

      const jaccard = questionJaccard(a.canonical_question, b.canonical_question);
      if (jaccard < 0.8) continue;

      // Check scope qualifier difference — if one has qualifier other doesn't, skip
      if (hasScopeQualifierDiff(a.canonical_question, b.canonical_question)) {
        logDebug("CrossSERP", `Question near-dup SKIPPED (scope qualifier diff): "${a.title}" vs "${b.title}" (jaccard=${jaccard.toFixed(2)})`);
        continue;
      }

      const [winner, loser] = pickWinner(a, b);
      loser.serp_validation = "merge";
      loser.serp_validation_reason = `Question dedup (jaccard=${jaccard.toFixed(2)}): duplikat "${winner.title}"`;
      mergedCount++;
      logDebug("CrossSERP", `Question dedup: merged "${loser.title}" → "${winner.title}" (jaccard=${jaccard.toFixed(2)})`);
    }
  }

  // --- C) Cluster detection (mark only, don't merge) ---
  const forCluster = active.filter((n) => n.serp_validation !== "merge");
  const clustered = new Set<ValidatedNodePreview>();

  for (let i = 0; i < forCluster.length; i++) {
    for (let j = i + 1; j < forCluster.length; j++) {
      const a = forCluster[i], b = forCluster[j];
      if (a.document_role !== b.document_role) continue;
      if (normQ(a.primary_entity) !== normQ(b.primary_entity)) continue;

      // Same best_market_keyword?
      const sameMarketKw = a.best_market_keyword && b.best_market_keyword
        && normQ(a.best_market_keyword) === normQ(b.best_market_keyword);

      // Shared URLs in serp_top3?
      const urlsA = new Set(a.serp_top3.map((c) => c.url));
      const sharedUrls = b.serp_top3.filter((c) => urlsA.has(c.url)).length;
      const sharedDomains = b.serp_top3.filter((c) => a.serp_top3.some((ac) => ac.domain === c.domain)).length;

      if (sameMarketKw || sharedUrls >= 2) {
        if (!clustered.has(a)) {
          a.serp_validation_reason += ` | cluster_candidate:${b.title}`;
          clustered.add(a);
        } else {
          a.serp_validation_reason += `|${b.title}`;
        }
        if (!clustered.has(b)) {
          b.serp_validation_reason += ` | cluster_candidate:${a.title}`;
          clustered.add(b);
        } else {
          b.serp_validation_reason += `|${a.title}`;
        }
        clusterPairs.push({ nodeA: a, nodeB: b });
        clusterCount++;
        logDebug("CrossSERP", `CLUSTER: "${a.title}" + "${b.title}" | shared_urls=${sharedUrls} shared_domains=${sharedDomains}`);
      }
    }
  }

  if (mergedCount > 0 || clusterCount > 0) {
    logPublic("CrossSERP", `${mergedCount} merged, ${clusterCount} cluster pairs`);
  }

  return clusterPairs;
}

// =============================================
// LLM Cluster Dedup — resolve cluster candidates via LLM
// =============================================

const CLUSTER_DEDUP_SCHEMA = {
  type: "object" as const,
  properties: {
    same_document: { type: "string" as const, enum: ["yes", "no", "maybe"] },
    confidence: { type: "number" as const },
    winner: { type: "string" as const, enum: ["a", "b"] },
    reason: { type: "string" as const },
  },
  required: ["same_document", "confidence", "winner", "reason"] as const,
  additionalProperties: false as const,
};

async function llmClusterDedup(
  results: ValidatedNodePreview[],
  clusters: ClusterPair[],
): Promise<void> {
  if (clusters.length === 0) return;

  // Deduplicate pairs — don't ask about the same node twice if already merged
  const merged = new Set<ValidatedNodePreview>();
  const uniquePairs = clusters.filter((p) => {
    if (merged.has(p.nodeA) || merged.has(p.nodeB)) return false;
    if (p.nodeA.serp_validation === "merge" || p.nodeB.serp_validation === "merge") return false;
    return true;
  });

  if (uniquePairs.length === 0) return;

  logPublic("LLM-Dedup", `${uniquePairs.length} cluster pairs...`);

  const dbPrompt = await loadPrompt("cluster_dedup" as any);
  const stageConfig = getStageConfig("cluster_dedup" as any);
  const model = dbPrompt?.modelId || stageConfig?.model || "gpt-4.1-mini";
  const provider = dbPrompt?.provider || stageConfig?.provider || "openai";
  const llm = getLLMProvider(provider);

  let mergedCount = 0;
  let keptCount = 0;
  let uncertainCount = 0;

  // Process pairs (sequential to avoid race conditions on shared nodes)
  for (const pair of uniquePairs) {
    if (pair.nodeA.serp_validation === "merge" || pair.nodeB.serp_validation === "merge") continue;

    const templateVars: Record<string, string> = {
      node_a_title: pair.nodeA.title,
      node_a_question: pair.nodeA.canonical_question,
      node_a_role: pair.nodeA.document_role,
      node_a_entity: pair.nodeA.primary_entity,
      node_a_market_kw: pair.nodeA.best_market_keyword || "",
      node_b_title: pair.nodeB.title,
      node_b_question: pair.nodeB.canonical_question,
      node_b_role: pair.nodeB.document_role,
      node_b_entity: pair.nodeB.primary_entity,
      node_b_market_kw: pair.nodeB.best_market_keyword || "",
    };

    const prompt = dbPrompt
      ? fillTemplate(dbPrompt.template, templateVars)
      : `Compare these two nodes and decide: same document or separate?\n\nA: "${pair.nodeA.title}" (${pair.nodeA.document_role})\nB: "${pair.nodeB.title}" (${pair.nodeB.document_role})\n\nReturn JSON: { "same_document": "yes|no|maybe", "confidence": 0-1, "winner": "a|b", "reason": "..." }`;

    try {
      const response = await llm.call(prompt, {
        model,
        temperature: 0.1,
        jsonSchema: CLUSTER_DEDUP_SCHEMA,
      });

      const result = parseLLMJSON<{ same_document: string; confidence: number; winner: string; reason: string }>(response.text);

      if (result.same_document === "yes" && result.confidence >= 0.75) {
        const loser = result.winner === "a" ? pair.nodeB : pair.nodeA;
        const winner = result.winner === "a" ? pair.nodeA : pair.nodeB;
        loser.serp_validation = "merge";
        loser.serp_validation_reason = `llm_cluster_dedup: ${result.reason} (conf=${result.confidence.toFixed(2)}) → "${winner.title}"`;
        merged.add(loser);
        mergedCount++;
        logDebug("LLM-Dedup", `MERGE: "${loser.title}" → "${winner.title}" (conf=${result.confidence.toFixed(2)})`);

      } else if (result.same_document === "maybe") {
        pair.nodeA.serp_validation_reason += ` | llm_uncertain: ${result.reason}`;
        pair.nodeB.serp_validation_reason += ` | llm_uncertain: ${result.reason}`;
        uncertainCount++;
        logDebug("LLM-Dedup", `UNCERTAIN: "${pair.nodeA.title}" + "${pair.nodeB.title}" (conf=${result.confidence.toFixed(2)})`);

      } else {
        // same_document="no" — remove cluster_candidate marker
        pair.nodeA.serp_validation_reason = pair.nodeA.serp_validation_reason.replace(/\s*\|\s*cluster_candidate:[^|]*/g, "");
        pair.nodeB.serp_validation_reason = pair.nodeB.serp_validation_reason.replace(/\s*\|\s*cluster_candidate:[^|]*/g, "");
        keptCount++;
        logDebug("LLM-Dedup", `KEEP: "${pair.nodeA.title}" + "${pair.nodeB.title}" (conf=${result.confidence.toFixed(2)})`);
      }
    } catch (err) {
      logError("LLM-Dedup", `Error for pair "${pair.nodeA.title}" + "${pair.nodeB.title}"`, err);
    }
  }

  logPublic("LLM-Dedup", `${mergedCount} merged, ${keptCount} kept, ${uncertainCount} uncertain`);
}

// =============================================
// Helper
// =============================================

function makeNeutral(
  c: NodePreview, reason: string,
  decision: SerpValidationDecision = "keep",
  conf: "high" | "medium" | "low" = "low",
  orig?: ProbeSVResult,
  decisionReasonType?: string,
): ValidatedNodePreview {
  const sv = orig?.strict_sv || 0;
  const cpc = orig?.strict_cpc || 0;
  const kd = orig?.strict_kd || 0;
  return {
    ...c,
    candidate_sv: sv, market_keyword_sv: 0, validated_sv: 0,
    original_search_volume: sv, original_cpc: cpc, original_keyword_difficulty: kd,
    validated_search_volume: 0, validated_cpc: 0, validated_keyword_difficulty: 0,
    serp_top3: [], market_keywords: [],
    serp_validation: decision, serp_validation_reason: reason, overlap_score: 0,
    original_question: c.canonical_question, validation_confidence: conf,
    search_volume: sv, display_sv: sv,
    cpc, keyword_difficulty: kd,
    demand_tier: "unknown" as DemandTier, related_keywords: [],
    entity_match_score: 0, role_match_score: 0, scope_similarity_score: 0,
    rewrite_confidence_score: 0, rewrite_block_reason: "", final_decision_source: "rules",
    best_market_keyword: "",
    inferred_candidate_intent: inferIntent(c.canonical_question),
    inferred_market_intent: "unknown" as InferredIntent,
    serp_mode: "informational" as SerpMode,
    role_preserved: true,
    entity_preserved: true,
    keep_confidence_reason: reason,
    // v9 probe debug
    strict_probe_winner: orig?.strict_winner || undefined,
    strict_probe_sv: orig?.strict_sv || 0,
    exploratory_probe_winner: orig?.exploratory_winner || undefined,
    exploratory_probe_sv: orig?.exploratory_sv || 0,
    candidate_sv_source: sv > 0 ? "strict_probe" : "no_strict_data",
    decision_reason_type: decisionReasonType || "no_market_data",
  };
}
